<script setup lang="ts">
import { computed } from "vue";

import { navigationActions } from "@/action/navigationActions";
import SidePanel from "@/components/SidePanel/SidePanel.vue";
import SidePanelContent from "@/components/SidePanel/SidePanelContent.vue";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import { useBoardSizeStore } from "@/store/boardSize";
import { useSessionStore } from "@/store/session";
import { useSwitchSessionStore } from "@/store/switchSession";
import { rangeText, sessionExecution } from "@/utils/session";

import OrganizationTree from "./internal/OrganizationTree.vue";

function handleClose() {
  useSwitchSessionStore().toggle(false);
}

const sessionName = computed(() => useSessionStore().current.name);
const currentSessionDateRange = computed(() =>
  rangeText(sessionExecution(useSessionStore().current)),
);

const goToSwitchSession = () => {
  useBoardSizeStore().resetScrollableMargin();
  navigationActions.openModal("sidebar-menu", "SessionSelectionModal");
  useSwitchSessionStore().toggle(false);
};
</script>

<template>
  <div class="switch-session-panel" data-ignore-click @scroll.stop>
    <SidePanel
      side="left"
      :active="useSwitchSessionStore().active"
      name="switchSession"
      resizable
      @close="handleClose"
    >
      <SidePanelContent v-focus-trap v-autofocus>
        <template #sub-title>
          <div class="switch-session-header-container">
            <div class="session-name">{{ sessionName }}</div>
            <div class="session-date">
              {{ currentSessionDateRange }}
            </div>
            <BaseButton
              variant="outlined"
              color="primary"
              size="small"
              class="switch-session-button"
              :aria-label="$t('switchSession.title')"
              @click="goToSwitchSession"
            >
              {{ $t("switchSession.title") }}
            </BaseButton>
          </div>
        </template>
        <div class="organization-container">
          <OrganizationTree />
        </div>
      </SidePanelContent>
    </SidePanel>
  </div>
</template>

<style lang="scss">
@use "@/styles/z-index";
@use "@/styles/variables/colors";
@use "@/styles/mixins/typography";

.switch-session-panel {
  position: relative;
  z-index: z-index.$menu;

  .switch-session-header-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 8px;
    width: 100%;

    .session-name {
      @include typography.medium("bold");

      color: colors.$text;
      max-width: 90%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .session-date {
      @include typography.small("semi-bold");

      color: colors.$text-subtle;
    }
  }

  .organization-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 4px;
  }
}
</style>
